import axios from 'axios';

// Use environment variable or default to localhost for development
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors with automatic retry
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401) {
      // If this is the refresh endpoint itself failing, immediately logout
      if (originalRequest.url?.includes('/auth/refresh')) {
        localStorage.removeItem('token');
        // Only redirect if not already on login page
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      // If already retried, logout
      if (originalRequest._retry) {
        localStorage.removeItem('token');
        // Only redirect if not already on login page
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Try to refresh the token
        const response = await api.post('/auth/refresh');
        const { access_token } = response.data;

        localStorage.setItem('token', access_token);
        api.defaults.headers.common['Authorization'] = 'Bearer ' + access_token;
        originalRequest.headers['Authorization'] = 'Bearer ' + access_token;

        processQueue(null, access_token);
        isRefreshing = false;

        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;

        // Refresh failed - logout user
        localStorage.removeItem('token');
        // Only redirect if not already on login page
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export const authApi = {
  login: (username, password) => {
    const credentials = btoa(`${username}:${password}`);
    return api.post('/auth/login', {}, {
      headers: {
        'Authorization': `Basic ${credentials}`
      }
    });
  },
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
  refreshToken: () => api.post('/auth/refresh'),
};

export const filesApi = {
  uploadFiles: (files, processingMode = 'simple', userFile = null) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    formData.append('processing_mode', processingMode);
    if (userFile) {
      formData.append('user_file', userFile);
    }
    return api.post('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  getUploadConfig: () => api.get('/files/upload-config'),
  getProcessingStatus: (fileId) => api.get(`/files/status/${fileId}`),
  getMyUploads: () => api.get('/files/my-uploads'),
  downloadFile: (fileId) => {
    return api.get(`/files/download/${fileId}`, {
      responseType: 'blob',
    });
  },
  getUserUsageStats: () => api.get('/files/usage/stats'),
  getUsageStats: () => api.get('/files/usage/stats'),
  getAllUsersUsage: (limit = 50) => api.get(`/files/usage/all-users?limit=${limit}`),
};

export const adminApi = {
  getStats: () => api.get('/admin/stats'),
  getUsers: () => api.get('/admin/users'),
  getUserSessions: (userId) => api.get(`/admin/users/${userId}/sessions`),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  createUser: (data) => api.post('/admin/users', data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),

  // Companies
  getCompanies: () => api.get('/admin/companies'),
  createCompany: (data) => api.post('/admin/companies', data),
  updateCompany: (companyId, data) => api.put(`/admin/companies/${companyId}`, data),
  deleteCompany: (companyId) => api.delete(`/admin/companies/${companyId}`),

  // Templates
  getTemplates: () => api.get('/admin/templates'),
  getTemplateDetail: (templateId) => api.get(`/admin/templates/${templateId}`),
  createTemplate: (data) => api.post('/admin/templates', data),
  updateTemplate: (templateId, data) => api.put(`/admin/templates/${templateId}`, data),
  deleteTemplate: (templateId) => api.delete(`/admin/templates/${templateId}`),
};

export default api;